import { useCallback, useEffect, useMemo, useState } from 'react';

import { GetListsRequestDTO, GetListsResponseDTO, ListColumnId, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { unparse } from 'papaparse';
import { v4 as uuidv4 } from 'uuid';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadIcon from '@/components/icons/download';
import PlusIcon from '@/components/icons/plus';
import ListDrawer from '@/components/lists/list-drawer';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { getListsInfiniteQueryOptions, listKeys } from '@/queries/lists';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';

import { ListsTable, NoListsCreatedDisplay, NoListsFoundFromFiltersDisplay, NoListsFoundFromSearchDisplay } from './table';
import { columns as listsColumns } from './table-meta';

function ListsPage() {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const { canManageLists, canExportCsv } = usePermissions();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Lists, {
    columns: listsColumns,
  });

  const [openListDrawer, setOpenListDrawer] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [selectedList, setSelectedList] = useState<GetListsResponseDTO['data'][number] | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: listsData,
    fetchNextPage: fetchNextListsPage,
    hasNextPage: hasNextListsPage,
    isFetching: isFetchingLists,
    isFetchingNextPage: isFetchingNextListsPage,
    error: listsError,
  } = useInfiniteQuery(
    getListsInfiniteQueryOptions({
      search: debouncedSearch,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetListsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const { mutate: deleteLists, isPending: isDeletingLists } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteLists({ listIds: ids }),
    onSuccess: () => {
      toast.success('Lists deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: listKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleDeleteLists() {
    if (listsData?.pages) {
      const toDelete = getSelectedLists(listsData);
      deleteLists(toDelete.map((list) => list.id));
    }
  }

  const handleExport = useCallback((list: GetListsResponseDTO['data'][number]) => {
    const csv = unparse(
      {
        data: ((list.elements || []) as string[])?.map((element) => ({ items: element })),
        fields: ['items'],
      },
      {
        header: false,
        skipEmptyLines: true,
      },
    );

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${list.name}.csv`;
    link.click();
  }, []);

  const getSelectedLists = useMemo(() => {
    return (incomingData: typeof listsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const lists = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => lists[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedLists = useMemo(() => getSelectedLists(listsData), [listsData, getSelectedLists]);

  useEffect(() => {
    if (listsError) {
      toast.error(listsError.message);
    }
  }, [toast, listsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextListsPage,
    hasNextPage: hasNextListsPage,
    isFetching: isFetchingLists,
    isError: !!listsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => listsData?.pages.flatMap((page) => page.data) ?? [], [listsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedLists.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Lists</TablePageTitle>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <ColumnsDialog<GetListsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={listsColumns}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: ListColumnId; active: boolean }> },
                        })
                      }
                    />
                  </div>
                  {canManageLists && (
                    <TablePageHeaderActionButton
                      variant="success"
                      icon={<PlusIcon />}
                      kind="primary"
                      onClick={() => setOpenListDrawer(true)}
                      label="Create List"
                    />
                  )}
                  <ListDrawer
                    noTrigger
                    open={openListDrawer}
                    onOpenChange={(open) => {
                      setOpenListDrawer(open);
                      if (!open) setSelectedList(null);
                    }}
                    id={selectedList?.id}
                    readOnly={!canManageLists}
                    defaultValues={
                      selectedList
                        ? {
                            name: selectedList.name,
                            description: selectedList.description || undefined,
                            elements: (selectedList?.elements as string[])?.map((element) => ({ value: element, id: uuidv4() })),
                          }
                        : undefined
                    }
                  />
                </div>
              </>
            )}

            {selectedLists.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedLists.length} totalCount={listsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && !(selectedLists.length > 1) && (
                  <TablePageHeaderActionButton
                    icon={<DownloadIcon />}
                    label="Download CSV"
                    onClick={() => handleExport(selectedLists[0])}
                    disabled={!selectedLists[0] || selectedLists.length > 1}
                  />
                )}
                {canManageLists && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete Selected"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <ListsTable
          data={flattenedData}
          loading={(isFetchingLists && !isFetchingNextListsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoListsFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoListsFoundFromFiltersDisplay openListDrawer={() => setOpenListDrawer(true)} removeColumnFilters={() => setModuleFilters(MODULE.Lists, [])} />
            ) : (
              <NoListsCreatedDisplay openListDrawer={() => setOpenListDrawer(true)} />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Lists, filters)}
          onRowClick={(row) => {
            // Both Admins and Members open the drawer on row-click; the drawer
            // itself renders read-only for Members via `canManageLists`.
            setOpenListDrawer(true);
            setSelectedList(row);
          }}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextListsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextListsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteLists}
        loading={isDeletingLists}
        title="Delete lists?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these lists?"
      />
    </div>
  );
}

export default ListsPage;
